/**
 * Long-form evergreen guides: county recording, easement law by state, and
 * property-rights questions. Same rules as blogPosts.ts — every statute cited
 * is linked to the official legislature or government source, and claims stay
 * general where state law varies.
 */
import type { BlogPost } from "./blogPosts";

const CA = (code: string, section: string) =>
  `https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=${code}&sectionNum=${section}`;

export const BLOG_GUIDES: BlogPost[] = [
  {
    slug: "how-to-search-county-recorder-for-easements",
    title: "How to search a county recorder's office for easements on your lot",
    publishedOn: "2026-09-30",
    topic: "County recording",
    summary:
      "A step-by-step walkthrough of the grantor-grantee index, plat books and instrument numbers, so you can pull every recorded easement for a lot yourself.",
    intro: [
      "Almost every easement that binds a residential lot in the United States is written down in one place: the land records kept by the county. Depending on the state, that office is called the recorder, the register of deeds, the county clerk, or the clerk of court. The name changes; the job does not. The office keeps a copy of every deed, mortgage, plat and easement that someone paid to record, and it indexes them so the public can find them.",
      "Title companies search these same records before a closing. You can too. It takes patience rather than special access, and knowing how the index works saves hours.",
    ],
    sections: [
      {
        heading: "Start with the right office and the right name",
        paragraphs: [
          "Land records are kept at the county level in nearly every state. A handful of New England states keep them by town instead, and Louisiana uses parish clerks. Before searching, confirm which office holds records for the lot's location, not the mailing address. A house with a city mailing address may sit in an unincorporated part of a neighboring county.",
          "Next, collect the names of everyone who has owned the lot. Recorder indexes are organized by party name, not by street address. The current deed usually names the prior owner as grantor, and that prior deed names the owner before them. Following that chain backward is how you find easements granted decades ago.",
        ],
      },
      {
        heading: "How the grantor-grantee index works",
        paragraphs: [
          "Every recorded document lists at least two parties. The grantor gives an interest and the grantee receives it. When a homeowner signs an easement to a power company, the homeowner is the grantor and the utility is the grantee. Searching the grantor index for each past owner's name surfaces every easement, mortgage and deed they signed.",
          "Look for document types labeled easement, right-of-way, grant of easement, dedication, declaration, covenants or plat. Many online indexes let you filter by document type. When a result looks relevant, note the instrument number or book and page. That reference is the permanent address of the document and is what a title commitment will cite in Schedule B.",
        ],
        bullets: [
          "Search each past owner as grantor, not only the current owner.",
          "Try spelling variants and middle initials; indexes are literal.",
          "Search the developer or builder's name for subdivision-wide easements.",
          "Record every instrument number you open so you can cite it later.",
        ],
      },
      {
        heading: "Do not skip the recorded plat",
        paragraphs: [
          "Most utility and drainage easements in a modern subdivision were never granted lot by lot. The developer drew them on the subdivision plat, and recording the plat created them for every lot at once. The plat is usually indexed by subdivision name, with a plat book and page or a cabinet and slide number.",
          "Your deed's legal description normally names the subdivision, block and lot. Use that to find the plat, then read the notes and the dashed or shaded strips along lot lines. Widths are commonly written right on the drawing, such as a 10-foot utility easement along the rear line.",
        ],
      },
      {
        heading: "Online portals, fees and copies",
        paragraphs: [
          "A growing number of counties publish their index and document images online. Some are free to search but charge a small per-page fee for copies; others require an account. Older records, often those recorded before the 1980s or 1990s, may only be available on microfilm at the office itself.",
          "If the portal is limited, call the office. Staff cannot give legal advice, but they can tell you how far back the online index goes, whether plats are scanned, and what copies cost. For a certified copy, ask specifically, because certified copies usually cost more than plain ones.",
        ],
      },
      {
        heading: "What a records search cannot show",
        paragraphs: [
          "The recorder only holds documents someone chose to record. Easements created by long use (prescriptive easements) or implied by how land was divided can exist with no document at all. A records search also does not tell you where an easement sits on the ground when the document describes it loosely, such as a blanket easement over the whole parcel.",
          "That is why a title commitment and, where location matters, a boundary survey remain the standard tools before buying or building.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Search by owner name through the full chain of title, not by street address.",
      "Pull the recorded subdivision plat; it holds most residential utility easements.",
      "Keep every instrument number so you can match it against Schedule B of a title commitment.",
      "Expect unrecorded easements to exist; a records search is a floor, not a ceiling.",
    ],
    sources: [
      {
        label: "U.S. Census Bureau. County and county-equivalent geography reference",
        url: "https://www.census.gov/programs-surveys/geography/about/glossary.html",
      },
      {
        label: "Cornell Law School Legal Information Institute: Easement",
        url: "https://www.law.cornell.edu/wex/easement",
      },
      {
        label: "Consumer Financial Protection Bureau: Buying a home tools and guides",
        url: "https://www.consumerfinance.gov/owning-a-home/",
      },
    ],
    relatedQuestions: [
      "how-to-find-easements-on-my-property",
      "how-to-read-a-title-commitment-for-easements",
      "what-is-a-blanket-easement",
      "do-i-need-a-survey-to-locate-an-easement",
    ],
  },
  {
    slug: "prescriptive-easement-time-periods-by-state",
    title: "Prescriptive easements: how long use takes to become a right in key states",
    publishedOn: "2026-09-30",
    topic: "State law",
    summary:
      "Prescriptive easements borrow their clock from each state's adverse possession statute. Here is how California, Texas, New York and Ohio set that clock, with the statutes linked.",
    intro: [
      "A prescriptive easement is a right to use someone else's land that arises from long, open use rather than from a signed document. A neighbor who has driven across the corner of your lot for decades may, in some circumstances, have a legal right to keep doing it.",
      "No federal law governs this. Each state sets its own rules, and most borrow the time period from their adverse possession statute. The elements courts look for are broadly similar across states, but the length of time is not.",
    ],
    sections: [
      {
        heading: "The elements courts usually require",
        paragraphs: [
          "Although wording differs, courts generally ask whether the use was open and notorious, continuous and uninterrupted, and adverse or hostile to the owner's rights for the full statutory period. Adverse does not mean angry. It means the use happened without the owner's permission. Use that began with permission typically cannot ripen into a prescriptive right unless the user later clearly repudiates that permission.",
          "Unlike adverse possession, a prescriptive easement does not transfer ownership. It gives a right to continue a specific use, such as crossing a strip on foot or by vehicle, and courts tend to limit it to the kind and intensity of use that actually occurred.",
        ],
      },
      {
        heading: "California: five years, and a sign that stops the clock",
        paragraphs: [
          "California courts apply a five-year period, drawn from the state's adverse possession statute in Code of Civil Procedure section 321 and related sections. California also gives owners an unusual tool. Under Civil Code section 1008, an owner may post signs at each entrance or at intervals along the boundary stating that the right to pass is by permission and subject to control of the owner. Properly posted signs prevent use from ripening into a prescriptive right.",
          "California separately recognizes that recorded notices can serve a similar purpose under Civil Code section 813, which lets an owner record a notice that use of land is by permission.",
        ],
      },
      {
        heading: "Texas: ten years under the adverse possession statute",
        paragraphs: [
          "Texas courts look to the ten-year limitations period in Civil Practice and Remedies Code section 16.026 when evaluating prescriptive easement claims. Texas courts are known for applying the elements strictly, especially the requirement that the use be exclusive of the owner's own use, so shared use of a road with the owner often defeats a claim.",
        ],
      },
      {
        heading: "New York: ten years, with a statutory definition of adverse",
        paragraphs: [
          "New York's Real Property Actions and Proceedings Law section 501 defines adverse possession and ties it to a ten-year period under the Civil Practice Law and Rules. New York courts use the same period for prescriptive easements. Section 543 of the same law also provides that certain de minimis, non-structural encroachments such as fences, hedges and shrubbery are deemed permissive, which narrows what can ripen into a right.",
        ],
      },
      {
        heading: "Ohio: twenty-one years",
        paragraphs: [
          "Ohio Revised Code section 2305.04 sets a twenty-one-year limitations period for recovering real property, and Ohio courts apply that period to prescriptive easements. Ohio also requires the claimant to prove the elements by clear and convincing evidence, a higher standard than in ordinary civil cases.",
        ],
      },
      {
        heading: "Why the paperwork still matters",
        paragraphs: [
          "Because prescriptive easements are not recorded until a court declares them or the parties sign an agreement, they will not appear in a title search. Title policies commonly exclude rights of parties in possession and matters a survey would reveal. If you see a worn path, a gravel drive or a fence line that does not match the deed, ask about it before closing.",
        ],
      },
    ],
    whatItMeansForYou: [
      "The time period depends on your state; five to twenty-one years in the states above.",
      "Written permission is the simplest way to keep a neighbor's use from becoming a right.",
      "In California, statutory signs or a recorded notice can stop the clock.",
      "An attorney licensed in your state should evaluate any actual claim.",
    ],
    sources: [
      { label: "California Civil Code section 1008 (permission signs)", url: CA("CIV", "1008") },
      { label: "California Civil Code section 813 (recorded notice of permission)", url: CA("CIV", "813") },
      { label: "California Code of Civil Procedure section 321", url: CA("CCP", "321") },
      {
        label: "Texas Civil Practice and Remedies Code chapter 16 (limitations)",
        url: "https://statutes.capitol.texas.gov/Docs/CP/htm/CP.16.htm",
      },
      {
        label: "New York RPAPL section 501 (adverse possession defined)",
        url: "https://www.nysenate.gov/legislation/laws/RPA/501",
      },
      {
        label: "New York RPAPL section 543 (de minimis encroachments)",
        url: "https://www.nysenate.gov/legislation/laws/RPA/543",
      },
      {
        label: "Ohio Revised Code section 2305.04",
        url: "https://codes.ohio.gov/ohio-revised-code/section-2305.04",
      },
    ],
    relatedQuestions: [
      "what-is-a-prescriptive-easement",
      "can-my-neighbor-use-my-driveway",
      "what-is-encroachment",
      "can-a-neighbor-block-my-easement",
    ],
    relatedStates: ["california", "texas", "new-york", "ohio"],
  },
  {
    slug: "utility-easement-vs-right-of-way-explained",
    title: "Utility easement vs. right-of-way: what each one lets others do on your land",
    publishedOn: "2026-09-30",
    topic: "Property rights",
    summary:
      "The two terms get used interchangeably, but a utility easement and a road right-of-way usually carry different scopes, owners and consequences for what you can build.",
    intro: [
      "Buyers see both phrases on surveys and title commitments and often assume they mean the same thing. Sometimes they do. The word right-of-way describes a right to pass across land, and many utility easements are written as rights-of-way. But in everyday real estate use, the terms usually point to different arrangements with different practical effects.",
    ],
    sections: [
      {
        heading: "What a utility easement typically is",
        paragraphs: [
          "A utility easement gives a utility provider the right to install, operate, maintain and replace lines or pipes in a defined strip, and to enter the strip to do that work. You still own the land. You can usually use it for lawn, gardens and other uses that do not interfere with the utility's rights.",
          "Many residential utility easements were created on the subdivision plat and run along rear or side lot lines. Others were granted directly by an earlier owner. The written grant controls the scope, so two easements that look identical on a map may allow very different activities.",
        ],
      },
      {
        heading: "What a road right-of-way typically is",
        paragraphs: [
          "A public road right-of-way is the full corridor reserved for a street, not just the paved surface. It often includes the sidewalk, the planting strip and a band of what looks like front yard. In some places the government owns that strip outright; in others it holds an easement for road purposes over land the adjoining owner still technically owns.",
          "Either way, the practical result is similar: the government can widen the road, add sidewalks or install utilities within the right-of-way, and private structures there may be removed. State transportation departments and counties publish right-of-way maps in some areas, but coverage is uneven.",
        ],
      },
      {
        heading: "Private rights-of-way",
        paragraphs: [
          "A private right-of-way, often called an access easement, lets one owner cross another's land, usually to reach a road. Shared driveways and flag-lot access strips are common examples. These are recorded like other easements, and maintenance duties depend on the written agreement or, if silent, on state law.",
        ],
      },
      {
        heading: "How to tell which one you have",
        paragraphs: [
          "Start with the survey or the recorded plat. Surveyors usually label public road right-of-way lines separately from easement strips, and they often note the width of the right-of-way from the road centerline. Utility easements are typically shown as shaded or dashed bands with a label naming the type and width.",
          "Next, read Schedule B of the title commitment. Each recorded easement is listed with its instrument number, so you can pull the document and see who holds it and what it allows. A road right-of-way created by dedication on the plat may not be listed separately, because it is part of the plat itself.",
          "Finally, ask the local permit or public works office. They can confirm where the public right-of-way ends on your street, which is often several feet behind the curb and sometimes behind the sidewalk. This single phone call prevents the most common mistake homeowners make: building a front fence or retaining wall on public land.",
        ],
      },
      {
        heading: "Why the difference matters when you build",
        paragraphs: [
          "Local permit offices generally will not approve permanent structures inside a public right-of-way. Fences, sheds and patios inside a utility easement may be allowed, but at your risk: if the utility needs access, it can typically remove what is in the way without paying for it. Before digging anywhere, federal law directs excavators to use the 811 one-call system so buried lines are marked.",
        ],
        bullets: [
          "Road right-of-way: expect no private structures; ask the city or county.",
          "Utility easement: structures possible, but removable; read the grant.",
          "Private access easement: keep the path open; check the maintenance terms.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Read the recorded grant; its words, not the label on a map, define the rights.",
      "Treat any public road right-of-way as off-limits for permanent improvements.",
      "Call 811 before digging, even inside your own yard.",
      "Ask the permit office where the right-of-way line sits before building a front fence.",
    ],
    sources: [
      {
        label: "Pipeline and Hazardous Materials Safety Administration: Call Before You Dig",
        url: "https://www.phmsa.dot.gov/safety-awareness/pipeline/call-you-dig",
      },
      {
        label: "Federal Highway Administration: Office of Real Estate Services (right-of-way)",
        url: "https://www.fhwa.dot.gov/real_estate/",
      },
      { label: "Common Ground Alliance: Call 811", url: "https://call811.com/" },
    ],
    relatedQuestions: [
      "easement-vs-right-of-way",
      "what-is-a-utility-easement-on-my-property",
      "can-i-build-a-fence-across-an-easement",
      "who-maintains-a-shared-driveway",
    ],
  },
  {
    slug: "can-utility-company-cut-trees-in-easement",
    title: "Can a utility company cut down trees in its easement? What owners can do",
    publishedOn: "2026-09-30",
    topic: "Property rights",
    summary:
      "Utilities usually have broad vegetation rights in their corridors, but the written easement, state rules and advance-notice practices shape what they can do and what you can ask for.",
    intro: [
      "Few things upset a homeowner faster than a crew showing up to take down a mature tree. Whether the utility can do it depends first on the easement it holds, and second on the state rules and federal reliability standards that govern line clearance.",
    ],
    sections: [
      {
        heading: "The grant is the starting point",
        paragraphs: [
          "Most utility easements include a clause allowing the holder to trim, cut or remove trees and other obstructions that interfere with the lines. Courts tend to read these clauses by their purpose, which is keeping the corridor safe and clear, rather than limiting the utility to the methods common when the easement was signed. In 2024 the Supreme Court of Ohio read a 1948 trim-and-remove clause to cover herbicide, for example.",
          "If the easement is narrow or silent on vegetation, the utility's rights may be more limited. Pull the recorded document before assuming either way.",
        ],
      },
      {
        heading: "Why transmission corridors are cleared aggressively",
        paragraphs: [
          "Large high-voltage transmission lines are subject to federal reliability standards overseen by the Federal Energy Regulatory Commission, including a vegetation management standard. Tree contact with transmission lines contributed to major regional blackouts, so utilities clear these corridors on a schedule and keep tall-growing species out.",
          "Distribution lines, the smaller lines along neighborhood streets, are generally governed by state utility commission rules and the utility's own tariff and vegetation program.",
        ],
      },
      {
        heading: "Notice, disputes and compensation",
        paragraphs: [
          "Many utilities provide door hangers or letters before scheduled work, and some state commissions require notice. If you get notice, you can often ask the utility's forestry department about pruning instead of removal, or about replacing a removed tree with a low-growing species.",
          "Compensation for trees removed within a valid easement is generally not owed, because the right was paid for when the easement was granted. Work outside the easement boundaries is different, and that is where a survey and the recorded width matter.",
        ],
        bullets: [
          "Ask for the easement document and width the crew is relying on.",
          "Request pruning or a replacement planting where safety allows.",
          "Contact your state public utility commission if notice rules were ignored.",
          "Photograph the trees and the line before work begins.",
        ],
      },
      {
        heading: "Documenting damage and getting help",
        paragraphs: [
          "If work goes beyond what you believe the easement allows, document it the same day. Take photos with a visible date, note crew and truck identification, and measure the distance from the line to the damaged area. Compare that distance with the recorded width.",
          "Send a written complaint to the utility's customer or forestry department and keep a copy. If the response is unsatisfactory, most state public utility commissions accept consumer complaints about regulated electric and gas utilities. Commissions cannot usually award property damages, but they can review whether the utility followed its own procedures and tariff.",
          "For damage clearly outside the easement, such as trees removed on the wrong side of a property line, a claim may be available against the utility or its contractor. A local real estate attorney can assess whether a trespass or property damage claim is worth pursuing.",
        ],
      },
      {
        heading: "Planting the right tree in the right place",
        paragraphs: [
          "The simplest way to avoid conflict is to keep tall-maturing trees out of the corridor. Many utilities publish planting guides showing which species fit under lines and how far larger trees should sit from the nearest conductor. Before planting, call 811 to have buried lines marked as well.",
        ],
      },
    ],
    whatItMeansForYou: [
      "A typical utility easement allows tree removal inside the corridor without payment.",
      "Work outside the recorded width is not covered; know where the line falls.",
      "You can often negotiate pruning or replacement with the utility's forestry team.",
      "Plant low-growing species near lines and call 811 before digging.",
    ],
    sources: [
      {
        label: "Supreme Court of Ohio: Corder v. Ohio Edison Co., 2024-Ohio-5432",
        url: "https://www.supremecourt.ohio.gov/rod/docs/pdf/0/2024/2024-Ohio-5432.pdf",
      },
      {
        label: "Federal Energy Regulatory Commission: Reliability standards",
        url: "https://www.ferc.gov/industries-data/electric/industry-activities/reliability",
      },
      {
        label: "USDA Forest Service: Urban and community forestry",
        url: "https://www.fs.usda.gov/managing-land/urban-forests/ucf",
      },
    ],
    relatedQuestions: [
      "can-a-utility-company-cut-down-my-trees",
      "can-i-plant-trees-in-a-utility-easement",
      "how-wide-is-a-utility-easement",
      "what-is-a-utility-easement-on-my-property",
    ],
    relatedStates: ["ohio"],
  },
  {
    slug: "drainage-easements-and-flooding-what-owners-should-know",
    title: "Drainage easements and flooding: what homeowners are responsible for",
    publishedOn: "2026-09-30",
    topic: "Property rights",
    summary:
      "Drainage easements keep stormwater moving between lots. Here is how they are created, who usually maintains them, and how they relate to FEMA flood zones.",
    intro: [
      "A swale along the back fence or a grate in the side yard is often more than landscaping. In many subdivisions it sits inside a recorded drainage easement that the whole neighborhood depends on. Blocking it can flood a neighbor, and in many places it can also violate the plat and local code.",
    ],
    sections: [
      {
        heading: "How drainage easements are created",
        paragraphs: [
          "Most are created on the recorded subdivision plat when the developer designs the stormwater system. The plat notes usually say who may enter the strip and whether structures, fill or fences are prohibited. Some are granted separately to a county, city or homeowners association.",
        ],
      },
      {
        heading: "Who maintains them",
        paragraphs: [
          "Responsibility varies. Plats and HOA declarations often make the lot owner responsible for routine upkeep, such as mowing and keeping the swale clear of debris, while the public entity or association handles pipes, inlets and ponds. Some local governments accept maintenance of drainage easements only when they were formally dedicated and accepted.",
          "If the plat or declaration is silent, local stormwater ordinances usually fill the gap. The public works or stormwater department can tell you who maintains a given easement.",
        ],
        bullets: [
          "Do not regrade, fill or build retaining walls in the swale without approval.",
          "Keep fences raised or open at the bottom if the plat allows fences at all.",
          "Report damaged inlets or pipes to the public works department.",
        ],
      },
      {
        heading: "Drainage easements are not flood zones",
        paragraphs: [
          "A drainage easement is a property right recorded in the land records. A flood zone is a mapping designation on FEMA's Flood Insurance Rate Maps that affects insurance requirements. A lot can have one without the other. You can look up a lot's FEMA flood zone for free at the FEMA Flood Map Service Center.",
          "Being inside a special flood hazard area generally means a federally backed mortgage will require flood insurance. A drainage easement by itself does not trigger that requirement.",
        ],
      },
      {
        heading: "Common neighbor disputes and how they are resolved",
        paragraphs: [
          "The most frequent drainage conflict begins when one owner changes grading, adds a patio or builds a fence that redirects water onto a neighbor. Many states apply a reasonable use rule to surface water, asking whether the change was reasonable given the circumstances. Others follow older rules that treat surface water very differently, so outcomes vary by state.",
          "Where a recorded drainage easement exists, the analysis is often simpler: the easement document and plat notes say what may be placed in the strip, and the holder, whether a city, county or HOA, can typically require removal of obstructions. Start by calling the holder rather than the neighbor; a code enforcement letter often resolves the issue faster than a lawsuit.",
          "Keep records of standing water with dated photos after storms. They help both the drainage holder and any insurer understand the problem.",
        ],
        bullets: [
          "Identify the holder of the easement on the plat.",
          "Ask the holder to inspect before confronting a neighbor.",
          "Photograph water flow during and after rain events.",
        ],
      },
      {
        heading: "Before you buy",
        paragraphs: [
          "Read the plat notes, ask the seller about any standing water after storms, and check the FEMA map. A survey will show the easement's location relative to the house, patio and fence lines.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Keep drainage strips open; blocking them can create liability to neighbors.",
      "Check the plat and HOA declaration for who maintains what.",
      "Look up the separate FEMA flood zone at the Flood Map Service Center.",
      "Ask public works before regrading or building near a swale.",
    ],
    sources: [
      { label: "FEMA Flood Map Service Center", url: "https://msc.fema.gov/portal/home" },
      {
        label: "U.S. Environmental Protection Agency: Stormwater and green infrastructure",
        url: "https://www.epa.gov/green-infrastructure",
      },
      {
        label: "FloodSmart (National Flood Insurance Program)",
        url: "https://www.floodsmart.gov/",
      },
    ],
    relatedQuestions: [
      "what-is-a-drainage-easement",
      "what-happens-if-i-build-on-an-easement",
      "can-i-build-a-fence-across-an-easement",
      "should-i-buy-a-house-with-an-easement",
    ],
  },
  {
    slug: "solar-easements-state-laws",
    title: "Solar easements and solar access laws: how states protect sunlight",
    publishedOn: "2026-09-30",
    topic: "State law",
    summary:
      "Some states let owners record a solar easement guaranteeing sunlight across a neighbor's land, and others limit HOA bans on panels. California and Florida show both approaches.",
    intro: [
      "The common law in most states gives no automatic right to sunlight across a neighbor's property. A neighbor can generally build or plant in a way that shades your roof. Several states address this with statutes that allow voluntary solar easements, restrict private covenants that ban solar equipment, or both.",
    ],
    sections: [
      {
        heading: "What a solar easement is",
        paragraphs: [
          "A solar easement is a written, recorded agreement in which one owner promises not to block sunlight to another owner's solar collectors. Because it is voluntary, the neighbor has to agree, and often is paid. Once recorded, it runs with the land and binds future owners like any other easement.",
          "Statutes that authorize solar easements usually require the document to describe the protected airspace, often by vertical and horizontal angles, and to address what happens if the easement is terminated.",
        ],
      },
      {
        heading: "California",
        paragraphs: [
          "California Civil Code section 801.5 defines a solar easement as the right of receiving sunlight across real property of another for a solar energy system and lists what the instrument must include. Separately, Civil Code section 714, part of the Solar Rights Act, makes covenants and restrictions that effectively prohibit or unreasonably restrict solar energy systems void and unenforceable, while allowing reasonable restrictions.",
        ],
      },
      {
        heading: "Florida",
        paragraphs: [
          "Florida Statutes section 163.04 prevents deed restrictions, covenants and similar binding agreements from prohibiting solar collectors and certain other energy devices on a building's roof, though associations may set the orientation within limits. Florida Statutes section 704.07 authorizes solar easements, and section 704.08 sets out what they must contain.",
        ],
      },
      {
        heading: "Negotiating a solar easement with a neighbor",
        paragraphs: [
          "Because solar easements are voluntary, the neighbor needs a reason to sign. Payment is common, and so is a mutual agreement in which each owner protects the other's roof. Some owners pair the easement with a promise to trim or remove specific trees at the solar owner's expense.",
          "The agreement should define the protected sky space using angles or a three-dimensional description, say whether trees existing at signing are grandfathered, explain how the easement ends if the solar system is removed, and specify any compensation. It should then be signed with the formalities your state requires for recording, typically notarization, and recorded in the county land records so it binds future owners of both parcels.",
          "Before investing in panels, look at the lot from the south. Sun path tools and the Department of Energy's consumer guidance can help estimate whether nearby trees or likely future buildings would shade the array.",
        ],
      },
      {
        heading: "Checking your own state",
        paragraphs: [
          "Many other states have solar access or easement statutes, and their details differ. Search your state legislature's website for solar easement or solar access, and check your HOA declaration, which may contain its own solar rules subject to state limits.",
        ],
        bullets: [
          "Record any solar easement with the county so it binds future owners.",
          "Describe the protected sky area precisely, not just the panel location.",
          "Review HOA rules against your state's solar rights statute.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Without a statute or agreement, you usually have no right to your neighbor's sunlight.",
      "A recorded solar easement is the most reliable way to protect a system.",
      "Some states void HOA rules that effectively ban solar panels.",
      "Have the easement drafted to your state's statutory requirements.",
    ],
    sources: [
      { label: "California Civil Code section 801.5 (solar easements)", url: CA("CIV", "801.5") },
      { label: "California Civil Code section 714 (Solar Rights Act)", url: CA("CIV", "714") },
      {
        label: "Florida Statutes section 163.04 (energy devices based on renewable resources)",
        url: "https://www.flsenate.gov/Laws/Statutes/2024/163.04",
      },
      {
        label: "Florida Statutes section 704.07 (solar easements)",
        url: "https://www.flsenate.gov/Laws/Statutes/2024/704.07",
      },
      {
        label: "U.S. Department of Energy: Homeowner's guide to going solar",
        url: "https://www.energy.gov/eere/solar/homeowners-guide-going-solar",
      },
    ],
    relatedQuestions: [
      "what-is-an-easement-on-my-property",
      "how-do-i-grant-an-easement-on-my-property",
      "do-easements-transfer-with-the-property",
      "what-does-it-cost-to-record-an-easement",
    ],
    relatedStates: ["california", "florida"],
  },
  {
    slug: "how-easements-end-abandonment-merger-release",
    title: "How easements end: release, merger, abandonment and expiration",
    publishedOn: "2026-09-30",
    topic: "State law",
    summary:
      "Easements usually last forever, but they can end by written release, by one owner acquiring both parcels, by abandonment, or by their own terms. California's statutes illustrate each path.",
    intro: [
      "Most easements are written to be permanent and to run with the land. That does not mean they can never end. Courts and legislatures recognize several ways an easement is extinguished, and knowing them helps when an old right-of-way no longer serves any purpose.",
    ],
    sections: [
      {
        heading: "Release by the holder",
        paragraphs: [
          "The cleanest path is a written release signed by the person or entity that holds the easement, then recorded with the county. Utilities sometimes release easements they no longer need, particularly after lines are relocated. Ask for a recordable release, not just a letter, so the title record is cleared.",
        ],
      },
      {
        heading: "Merger",
        paragraphs: [
          "An easement exists for one parcel over another. If the same person comes to own both the benefited and the burdened parcel, the easement generally ends by merger, because nobody needs an easement over their own land. California Civil Code section 811 lists merger among the ways a servitude is extinguished. If the parcels are later split again, the easement does not automatically revive; a new grant may be needed.",
        ],
      },
      {
        heading: "Abandonment and nonuse",
        paragraphs: [
          "Mere nonuse usually does not end an easement created by a deed. Most courts require nonuse plus clear evidence of intent to abandon, such as the holder removing a gate, fencing the path off, or stating in writing that the right is given up.",
          "Some states add statutory tools. California Civil Code sections 887.010 through 887.090 allow a court to declare an easement abandoned if it has not been used for twenty years and meets the other statutory conditions. An easement acquired by prescription, by contrast, can be lost by nonuse for the prescriptive period under Civil Code section 811.",
        ],
      },
      {
        heading: "Clearing an easement from the title record",
        paragraphs: [
          "Even when an easement has clearly ended, it may keep appearing on title commitments until something is recorded that removes it. Title insurers are cautious; they will usually continue to list an easement as an exception unless they see a recorded release, a court judgment, or clear evidence of merger in the chain of title.",
          "When the holder is a utility, start with its real estate or right-of-way department and ask whether the line is still in service. When the holder is a long-gone company or an individual who cannot be found, a quiet title action asks a court to declare the easement extinguished. The resulting judgment is recorded, and future title searches will show it.",
          "Relocation is sometimes an easier answer than extinguishment. Some states allow a court to approve relocating an easement at the burdened owner's expense when the change does not harm the holder, and many holders will agree to relocate voluntarily if you pay the cost.",
        ],
      },
      {
        heading: "Expiration and purpose",
        paragraphs: [
          "An easement granted for a set term or a specific purpose ends when the term runs out or the purpose is fulfilled. Easements by necessity typically end when the necessity ends, for example when a landlocked parcel gains its own road access.",
        ],
        bullets: [
          "Look for end dates or purpose limits in the recorded grant.",
          "Request a recorded release from the holder where possible.",
          "Use a quiet title action when the holder cannot be found.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Unused is not the same as ended; most easements need a release or court order.",
      "A recorded release clears the title record for future buyers.",
      "Owning both parcels usually merges the easement away.",
      "A quiet title action is the fallback for abandoned or orphaned easements.",
    ],
    sources: [
      { label: "California Civil Code section 811 (extinguishment of servitudes)", url: CA("CIV", "811") },
      { label: "California Civil Code section 887.050 (abandoned easements)", url: CA("CIV", "887.050") },
      {
        label: "Cornell Law School Legal Information Institute: Easement",
        url: "https://www.law.cornell.edu/wex/easement",
      },
    ],
    relatedQuestions: [
      "can-an-easement-expire",
      "can-i-remove-an-easement-from-my-property",
      "what-is-an-easement-by-necessity",
      "can-an-easement-be-moved",
    ],
    relatedStates: ["california"],
  },
  {
    slug: "conservation-easements-guide-for-landowners",
    title: "Conservation easements: how they work, what they cost you, and the tax rules",
    publishedOn: "2026-09-30",
    topic: "Property rights",
    summary:
      "A conservation easement permanently limits development in exchange for conservation value and sometimes a tax deduction. Here is what landowners and buyers should understand.",
    intro: [
      "A conservation easement is a voluntary, recorded agreement in which a landowner gives up some development rights, typically to a land trust or government agency, to protect farmland, habitat, scenic views or water quality. The owner keeps the land and can usually keep farming, living on or selling it, but every future owner is bound by the restrictions.",
    ],
    sections: [
      {
        heading: "What gets restricted",
        paragraphs: [
          "Each easement is negotiated, so terms vary widely. Common restrictions limit subdivision, new houses, commercial uses and mining, while reserving existing homes, agriculture and sometimes a small number of future building sites. The holder monitors the land, usually annually, to confirm compliance.",
        ],
      },
      {
        heading: "Federal tax treatment",
        paragraphs: [
          "Donating a qualified conservation easement to a qualified organization can support a federal income tax deduction under Internal Revenue Code section 170(h), provided the easement is perpetual and serves a recognized conservation purpose. The IRS has flagged abusive syndicated conservation easement transactions, and it requires a qualified appraisal and specific documentation.",
        ],
      },
      {
        heading: "Selling an easement instead of donating it",
        paragraphs: [
          "Some programs pay for easements. The USDA Natural Resources Conservation Service's Agricultural Conservation Easement Program helps fund purchases of easements on working farmland and wetlands, often in partnership with state programs and land trusts.",
        ],
      },
      {
        heading: "Effect on value and property taxes",
        paragraphs: [
          "Because development rights are removed, a conservation easement usually lowers the market value of the land. That reduction is what an appraiser measures for a tax deduction: the difference between the value before and after the easement is placed.",
          "Local property tax treatment varies. Some states direct assessors to account for the easement's restrictions, which can lower the assessed value, and many rural parcels already qualify for agricultural use assessment regardless of an easement. Ask the county assessor how easement-restricted land is valued in your jurisdiction.",
          "Estate planning is another reason owners choose conservation easements. A lower value can reduce estate tax exposure, and federal law contains a separate exclusion for certain land subject to a qualified conservation easement. These rules are technical and change, so owners typically work with a tax advisor and the land trust together.",
          "Finally, think about the long run. The easement will outlast you and any buyer. Choose a holder with the resources to monitor and defend it, and make sure the reserved rights leave room for the way the next generation may want to use the land.",
        ],
      },
      {
        heading: "Buying land with a conservation easement",
        paragraphs: [
          "The easement will appear in the title commitment. Read the full recorded document, not a summary, and ask the holder for the baseline documentation report and the most recent monitoring report. Confirm that the house, barn or use you plan fits within the reserved rights.",
        ],
        bullets: [
          "Get the baseline report showing conditions at the time of the grant.",
          "Ask the holder about any past violations or pending approvals.",
          "Expect a lower price than comparable unrestricted land.",
        ],
      },
    ],
    whatItMeansForYou: [
      "Conservation easements are permanent in most cases and bind every future owner.",
      "A donated easement may support a federal deduction if IRS rules are met.",
      "Paid programs such as USDA ACEP exist for qualifying farmland.",
      "Buyers should read the full easement and the holder's monitoring reports.",
    ],
    sources: [
      {
        label: "Internal Revenue Service: Conservation easements",
        url: "https://www.irs.gov/charities-non-profits/conservation-easements",
      },
      {
        label: "USDA NRCS: Agricultural Conservation Easement Program",
        url: "https://www.nrcs.usda.gov/programs-initiatives/acep-agricultural-conservation-easement-program",
      },
      {
        label: "Cornell Law School LII: 26 U.S. Code section 170",
        url: "https://www.law.cornell.edu/uscode/text/26/170",
      },
    ],
    relatedQuestions: [
      "what-is-a-conservation-easement",
      "does-an-easement-lower-property-value",
      "can-i-be-paid-for-an-easement-on-my-land",
      "does-an-easement-affect-property-taxes",
    ],
  },
];
